import { json, checkStatus, CustomerAPI, AccountsAPI, ConversationAPI } from '@driftt/dapper'

export const api = {
  fetchSFDCAccountListSyncHistory() {
    return AccountsAPI.get('/salesforce/sync?showresults=true')
      .then(json)
  },

  fetchAllSalesforceCampaigns() {
    return CustomerAPI
      .get(`/salesforce/campaigns/list`)
      .then(checkStatus)
      .then(json)
  },

  resyncAccountList() {
    return AccountsAPI.get('/salesforce/lists/sync')
      .then(json)
  },

  setForceResync() {
    return AccountsAPI
      .post(`/salesforce/toggles`, [{ "name":"forceSync","value":true }])
      .then(checkStatus)
      .then(json)
  },

  getAccountToggles() {
    return AccountsAPI
      .get(`/salesforce/toggles`)
      .then(checkStatus)
      .then(json)
  },

  setAccountDeleteOk(value) {
    return AccountsAPI
      .post(`/salesforce/toggles`, [{ "name":"deleteOk","value":value }])
      .then(checkStatus)
      .then(json)
  },

  fetchSFDCAccountLists() {
    return AccountsAPI.get('/salesforce/lists')
      .then(json)
  },

  fetchSyncedSFDCAccountLists() {
    return AccountsAPI.get('/salesforce/properties')
      .then(json)
  },

  fetchSFDCAccountProperties() {
    return AccountsAPI.get('/salesforce/fields')
      .then(checkStatus)
      .then(json)
  },

  saveSyncedSFDCAccountLists(lists) {
    return AccountsAPI.post('/salesforce/properties', lists)
      .then(json)
  },

  deleteSyncedSFDCAccountList(id) {
    return AccountsAPI.delete(`/salesforce/property/${id}`)
      .then(json)
  },

  fetchSFDCMappings() {
    return CustomerAPI.get('/salesforce/mappings/attributes/v2')
      .then(checkStatus)
      .then(json)
  },

  patchSFDCMappings(mappings, useRawMapping) {
    const payload = useRawMapping ? mappings : { mappings }
    return CustomerAPI.patch('/salesforce/mappings/attributes/v2', payload)
      .then(checkStatus)
      .then(json)
  },

  fetchSFDCMetricsMappings() {
    return CustomerAPI.get('/salesforce/mappings/attributes/metrics')
      .then(checkStatus)
      .then(json)
  },

  patchSFDCMetricsMappings(mappings) {
    return CustomerAPI.patch('/salesforce/mappings/attributes/metrics', { mappings })
      .then(checkStatus)
      .then(json)
  },

  fetchSFDCProperties() {
    return CustomerAPI.get('/salesforce/lead/properties')
      .then(checkStatus)
      .then(json)
  },

  fetchSFDCContactProperties() {
    return CustomerAPI.get('/salesforce/contact/properties')
      .then(checkStatus)
      .then(json)
  },

  fetchSFDCTaskProperties() {
    return CustomerAPI.get('/salesforce/task/properties')
      .then(checkStatus)
      .then(json)
  },

  fetchPropertyPreviews(properties) {
    return CustomerAPI.post('/property_definitions/preview', properties)
      .then(checkStatus)
      .then(json)
  },

  searchSFDCUsers(query) {
    return CustomerAPI.get(`/salesforce/users?query=${query}`)
      .then(checkStatus)
      .then(json)
  },

  fetchSFDCUserMappings() {
    return CustomerAPI.get('/salesforce/mappings/users')
      .then(checkStatus)
      .then(json)
  },

  fetchSFDCAccountPropertyMappings() {
    return AccountsAPI.get('/attrib/mapping')
      .then(checkStatus)
      .then(json)
  },

  fetchDriftAccountProperties() {
    return AccountsAPI.get('/attrib')
      .then(checkStatus)
      .then(json)
  },

  patchSFDCAccountPropertyMappings(mappings) {
    return AccountsAPI.patch('/attrib/mapping', mappings)
      .then(checkStatus)
      .then(json)
  },

  deleteSFDCAccountPropertyMappings(mappings) {
    return AccountsAPI.delete('/attrib/mapping/multi', mappings)
      .then(checkStatus)
      .then(json)
  },

  patchSFDCUserMappings(data) {
    return CustomerAPI.patch('/salesforce/mappings/users', data)
      .then(checkStatus)
      .then(json)
  },

  fetchSalesforceLeadPushCampaign() {
    return CustomerAPI
      .get(`/salesforce/campaigns/push`)
      .then(checkStatus)
      .then(json)
  },

  getSalesforceAutoMappings(apply) {
    return CustomerAPI
      .get(`/salesforce/mappings/users/automatic?apply=${apply}`)
      .then(checkStatus)
      .then(json)
  },

  fetchSalesforceCampaignById(id) {
    return CustomerAPI
      .get(`/salesforce/campaigns/${id}`)
      .then(checkStatus)
      .then(json)
  },

  replaceSalesforceLeadPushCampaign(data) {
    return CustomerAPI
      .put(`/salesforce/campaigns/push`, data)
      .then(checkStatus)
      .then(json)
  },

  searchSalesforceCampaigns(query) {
    return CustomerAPI
      .get(`/salesforce/campaigns/search?q=${query}`)
      .then(checkStatus)
      .then(json)
  },

  getSalesforceTaskTypeList() {
    return CustomerAPI
      .get('/salesforce/task/type/list')
      .then(checkStatus)
      .then(json)
      .then(data => data.taskTypes)
  },

  getSalesforceTaskType() {
    return CustomerAPI
      .get('/salesforce/task/type')
      .then(checkStatus)
      .then(json)
      .then(data => data.taskType)
  },

  getSalesforceTaskTypePhone() {
    return CustomerAPI
      .get('/salesforce/task/type/phone')
      .then(checkStatus)
      .then(json)
      .then(data => data.taskType)
  },

  getSalesforceInfluenceCampaignId() {
    return CustomerAPI
      .get('/salesforce/campaigns/influence')
      .then(checkStatus)
      .then(json)
  },

  setSalesforceInfluenceCampaignId({ campaignId }) {
    return CustomerAPI
      .put('/salesforce/campaigns/influence', { campaignId })
      .then(checkStatus)
      .then(json)
      .then(data => data.taskType)
  },

  patchSalesforceTaskType({ taskType }) {
    return CustomerAPI
      .patch('/salesforce/task/type', { taskType })
      .then(checkStatus)
      .then(json)
  },

  patchSalesforceTaskTypePhone({ taskType }) {
    return CustomerAPI
      .patch('/salesforce/task/type/phone', { taskType })
      .then(checkStatus)
      .then(json)
  },

  getSalesforceObjectList() {
    return CustomerAPI
      .get('/salesforce/object/list')
      .then(checkStatus)
      .then(json)
  },

  getSalesforceFieldList(fieldType) {
    return CustomerAPI
      .get(`/salesforce/object/${fieldType}/field/list/v2`)
      .then(checkStatus)
      .then(json)
  },

  getSalesforceLeadRoutingSettings() {
    return CustomerAPI
      .get('/salesforce/routing/owner/settings')
      .then(checkStatus)
      .then(json)
  },

  patchSalesforceLeadRoutingSettings({ settings }) {
    return CustomerAPI
      .patch('/salesforce/routing/owner/settings', { settings })
      .then(checkStatus)
      .then(json)
  },

  fetchSalesforceEnrichmentByEmail({ email }) {
    return ConversationAPI
      .get(`/salesforce/enrichment?email=${encodeURIComponent(email)}`)
      .then(checkStatus)
      .then(json)
  },

  fetchSalesforceEnrichmentByDomain({ domain }) {
    return ConversationAPI
      .get(`/salesforce/enrichment?domain=${encodeURIComponent(domain)}`)
      .then(checkStatus)
      .then(json)
  },

  fetchSalesforceEnrichmentFields() {
    return CustomerAPI
      .get(`/salesforce/enrichment/fields`)
      .then(checkStatus)
      .then(json)
  },

  patchSalesforceEnrichmentFields({ fields }) {
    return CustomerAPI
      .patch('/salesforce/enrichment/fields', { fields })
      .then(checkStatus)
      .then(json)
  },
  patchSalesforceEventType(data) {
    return CustomerAPI
      .patch('/salesforce/event/type',data)
      .then(checkStatus)
      .then(json)
  },
  getSalesforceEventTypeList() {
    return CustomerAPI
      .get('/salesforce/event/type/list')
      .then(checkStatus)
      .then(json)
      .then(data => data.eventTypes)
  },
  getSalesforceEventType() {
    return CustomerAPI
      .get('/salesforce/event/type')
      .then(checkStatus)
      .then(json)
      .then(data => data.eventType)
  },

  getSalesforceCampaigns() {
    return CustomerAPI
      .get(`/salesforce/campaigns/list`).then(json)
  },

  getSelectedSalesforceCampaign(playbookId) {
    return ConversationAPI.get(`/salesforce/playbook/campaign/${playbookId}`).then(json)
  },

  setSalesforceCampaign(playbookPayload) {
    return ConversationAPI.post('/salesforce/playbook/campaign', playbookPayload, {
      headers: { 'Content-Type': 'application/json' }
    }).then(json)
  },

  fetchCampaigns(query) {
    return CustomerAPI.get(`/salesforce/campaigns/search?q=${query}`).then(json)
  },

  fetchSFDCConnection() {
    return ConversationAPI.get('/salesforce/playbook/campaign/connected').then(checkStatus).then(json)
  },

  deleteSalesforceCampaign(playbookId) {
    return ConversationAPI.delete(`/salesforce/playbook/campaign/${playbookId}`, {
      headers: { 'Content-Type': 'application/json' }
    }).then(json)
  },

  getSalesforceMeetingEventFields() {
    return CustomerAPI.get('/salesforce/event/fields')
      .then(json)
  },

  getSalesforceBookedByField() {
    return CustomerAPI.get('/salesforce/stored/event/field').then(checkStatus).then(json)
  },

  setSalesforceBookedByField(meetingEventField) {
    return CustomerAPI.post('/salesforce/event/fields', meetingEventField, {
      headers: { 'Content-Type': 'application/json' }})
      .then(json)

  },
  deleteSalesforceBookedByField() {
    return CustomerAPI.delete('/salesforce/stored/event/field').then(checkStatus)
  },
}


// WEBPACK FOOTER //
// ./src/scripts/app/modules/sfdc/api.js